import { useEffect } from 'react';
import { X } from 'lucide-react';

const SHORTCUTS = [
  { keys: ['g', 'n'],          desc: 'Go to notifications' },
  { keys: ['j'],               desc: 'Focus next notification' },
  { keys: ['k'],               desc: 'Focus previous notification' },
  { keys: ['e'],               desc: 'Mark focused as read' },
  { keys: ['Enter'],           desc: 'Open focused notification' },
  { keys: ['Delete'],          desc: 'Soft-delete focused notification' },
  { keys: ['Esc'],             desc: 'Close dropdown / dialog' },
  { keys: ['?'],               desc: 'Show this help' },
];

/**
 * Lightweight modal listing keyboard shortcuts. Closes on Escape and on
 * backdrop click. Pure presentation — the caller owns open/close state.
 *
 * Props:
 *   open:    boolean
 *   onClose: () => void
 */
export function KeyboardShortcutsHelp({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Keyboard shortcuts</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <ul className="divide-y divide-gray-100 px-5 py-3">
          {SHORTCUTS.map((s) => (
            <li key={s.keys.join('+')} className="flex items-center justify-between py-2 text-sm">
              <span className="text-gray-700">{s.desc}</span>
              <span className="flex items-center gap-1">
                {s.keys.map((k) => (
                  <kbd
                    key={k}
                    className="rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 text-xs font-mono text-gray-700 shadow-sm"
                  >
                    {k}
                  </kbd>
                ))}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
